import * as React from 'react';
// Import material ui modules.
import {
	Check,
} from '@mui/icons-material';
// Import hooks.
import {
	UseSubformProducerAgreementResults,
	UseSubformProducerCompanyDetailsResults,
	UseSubformProducerErrorsAndOmissionsResults,
	UseSubformProducerPrimaryContactResults,
	useSubformProducerAgreement,
	useSubformProducerCompanyDetails,
	useSubformProducerErrorsAndOmissions,
	useSubformProducerPrimaryContact,
} from '@app/hooks/view';
// Import props.
import {
	FormProducerSignupProps,
	FormProps,
	NavigationFooterProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
} from '@app/enumerations';
// Import api models.
import {
	Address,
	NewProducer,
} from '@app/models/api';

interface UseFormProducerSignupParameters {
	id: string;
	props: FormProducerSignupProps,
	showValidations: boolean,
}

interface UseFormProducerSignupResults {
	subformProducerCompanyDetails: UseSubformProducerCompanyDetailsResults;
	subformProducerErrorsAndOmissions: UseSubformProducerErrorsAndOmissionsResults;
	subformProducerPrimaryContact: UseSubformProducerPrimaryContactResults;
	subformProducerAgreement: UseSubformProducerAgreementResults;
	applyFormProps: () => FormProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
}

const useFormProducerSignup = (params: UseFormProducerSignupParameters): UseFormProducerSignupResults => {

	// #region exported methods

	/**
	 *  Starts the submission process
	 **/
	const submitForm = (): void => {
		// First flag all inputs from all subforms to show any validation errors.
		flagAllInputsToShowValidations();
		// Validate the whole form.
		const isValid: boolean = checkFormValidationWithFocus();
		// Make sure the confirmation popup appears if it's required.
		if (isValid) {
			submitFormWithCompanyAddressVerification();
		}
	}
	
	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormProps => {
		return {
			id: generateIdForForm(),
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: false,
		};
	};

	/**
		 * Returns props for the navigation footer. 
		 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		return {
			id: generateIdForNavigationFooter(),
			rightButtonDisabled: !subformProducerAgreement.acknowledgementInput.value,
			rightButtonLabel: 'Sign Up',
			rightButtonClick: submitForm,
			rightButtonIcon: Check,
			alignment: NavigationFooterAlignment.RIGHT,
		};
	};

	// #endregion exported methods


	// #region private methods
	const finishConfirmCompanyAddressVerification = (address: Address): void => {
		if(subformProducerCompanyDetails.isCompanyAddressMailingAddress()) {
			proceedWithFormSubmissionForCompanyAddress(address);
		}
		else {
			subformProducerCompanyDetails.companyAddressVerification.addressLocationInput.setAddress(address);
			setProceedToVerifyMailingAddress((currentProceedToVerifyMailingAddress: boolean)=> {
				return true;
			});
		}
	}

	const finishConfirmMailingAddressVerification = (mailingAddress: Address): void => {
		proceedWithFormSubmissionForMailingAddress(mailingAddress);
	}

	const proceedWithFormSubmissionForCompanyAddress = (companyAddress: Address): void => {
		const newProducer: NewProducer = createNewProducer(companyAddress, companyAddress);
		params.props.onSignup(newProducer);
	}

	const proceedWithFormSubmissionForMailingAddress = (mailingAddress: Address): void => {
		const newProducer: NewProducer = createNewProducer(subformProducerCompanyDetails.companyAddressVerification.addressLocationInput.value.address, mailingAddress);
		params.props.onSignup(newProducer);
	}

	/**
	 * Starts the submission process without validation for the address. 
	 **/
	const submitFormWithCompanyAddressVerification = (): void => {
		// Proceed forward if form validates successfully.
		try {
			if (subformProducerCompanyDetails.companyAddressVerification.addressLocationInput.value.address !== undefined) {
				// If the input address components are already provided, then we need to verify them as address components.
				subformProducerCompanyDetails.companyAddressVerification
					.verifyAddressAsComponentsAsync(subformProducerCompanyDetails.companyAddressVerification.addressLocationInput.value.address);
			}
			else if (!String.isNullOrWhiteSpace(subformProducerCompanyDetails.companyAddressVerification.addressLocationInput.value.addressSingleLine)) {
				// If only a single line address input is provided, then we need to verify that first.
				subformProducerCompanyDetails.companyAddressVerification
					.verifyAddressAsSingleLineAsync(subformProducerCompanyDetails.companyAddressVerification.addressLocationInput.value.addressSingleLine);
			}
		}
		catch (error: any) {
			console.log(error);
		}
	}

	const submitFormWithMailingAddressVerification = (): void => {
		// Proceed forward if form validates successfully.
		try {
			if (subformProducerCompanyDetails.mailingAddressVerification.addressLocationInput.value.address !== undefined) {
				// If the input address components are already provided, then we need to verify them as address components.
				subformProducerCompanyDetails.mailingAddressVerification
					.verifyAddressAsComponentsAsync(subformProducerCompanyDetails.mailingAddressVerification.addressLocationInput.value.address);
			}
			else if (!String.isNullOrWhiteSpace(subformProducerCompanyDetails.mailingAddressVerification.addressLocationInput.value.addressSingleLine)) {
				// If only a single line address input is provided, then we need to verify that first.
				subformProducerCompanyDetails.mailingAddressVerification
					.verifyAddressAsSingleLineAsync(subformProducerCompanyDetails.mailingAddressVerification.addressLocationInput.value.addressSingleLine);
			}
		}
		catch (error: any) {
			console.log(error);
		}
	}

	const createNewProducer = (companyAddress: Address | undefined, mailingAddress: Address): NewProducer => {
		const newProducer: NewProducer = {
			companyName: subformProducerCompanyDetails.companyNameInput.value, 
			businessType_Id: parseInt(subformProducerCompanyDetails.businessTypeInput.value),
		    physicalAddress: companyAddress ?? null,
			mailingAddress: mailingAddress,
			phone:  subformProducerCompanyDetails.phoneInput.value,
			fax: subformProducerCompanyDetails.faxInput.value,
			nationalProducerLicenseNumber: subformProducerCompanyDetails.nationalProducerLicenseNumberInput.value,
			residentStateLicenseNumber: subformProducerCompanyDetails.residentInsuranceLicenseNumberInput.value,
			taxId: subformProducerCompanyDetails.taxIdInput.value,
			producerAssociationId: subformProducerCompanyDetails.producerAssociationInput.value === '0' ? null :
				parseInt(subformProducerCompanyDetails.producerAssociationInput.value),
			externalProducerId: subformProducerCompanyDetails.externalAgencyIdInput.value,
			errorsAndOmissionsData: {
				carrierName: subformProducerErrorsAndOmissions.carrierNameInput.value,
				policyExpirationDate: subformProducerErrorsAndOmissions.policyExpirationDateInput.value ?? null,
				policyNumber: subformProducerErrorsAndOmissions.policyNumberInput.value,
				limitPerClaim: subformProducerErrorsAndOmissions.limitPerClaimInput.value ?? null,
				aggregateLimit: subformProducerErrorsAndOmissions.aggregateLimitInput.value ?? null,
			},
			firstName: subformProducerPrimaryContact.firstNameInput.value,
			lastName: subformProducerPrimaryContact.lastNameInput.value,
			emailAddress: subformProducerPrimaryContact.emailInput.value,
			password: subformProducerPrimaryContact.newPasswordInput.passwordInput.value,
			heardThrough_Id: parseInt(subformProducerAgreement.heardThroughOptionInput.value), 
		}
		return newProducer;
	}

	

	/**
	 * Method to check form validation with focus. 
	 **/
	const checkFormValidationWithFocus = (): boolean => {
		return subformProducerCompanyDetails.checkFormValidationWithFocus() 
			&& subformProducerErrorsAndOmissions.checkFormValidationWithFocus()
			&& subformProducerPrimaryContact.checkFormValidationWithFocus()
			&& subformProducerAgreement.checkFormValidationWithFocus();
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		subformProducerCompanyDetails.flagAllInputsToShowValidations();
		subformProducerErrorsAndOmissions.flagAllInputsToShowValidations();
		subformProducerPrimaryContact.flagAllInputsToShowValidations();
		subformProducerAgreement.flagAllInputsToShowValidations();
	}

	//#region id generators
	const generateIdForForm = (): string => {
		return params.id;
	}

	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	const generateIdForSubformProducerCompanyDetails = (): string => {
		return `${generateIdForForm()}-subformProducerCompanyDetails`;
	}

	const generateIdForProducerErrosAndOmissionsSubform = (): string => {
		return `${generateIdForForm()}-subformProducerErrorsAndOmissions`;
	}

	const generateIdForSubformProducerPrimaryContact = (): string => {
		return `${generateIdForForm()}-subformProducerPrimaryContact`;
	}

	const generateIdForAgreementSubform = (): string => {
		return `${generateIdForForm()}-subformProducerAgreement`;
	}
	//#endregion id generators

	// #endregion private methods


	// #region variables
	const subformProducerCompanyDetails = useSubformProducerCompanyDetails({
		id: generateIdForSubformProducerCompanyDetails(),
		showValidations: params.showValidations,
		props: params.props,
		finishConfirmCompanyAddressVerification: finishConfirmCompanyAddressVerification,
		finishConfirmMailingAddressVerification: finishConfirmMailingAddressVerification,
	});
	
	const subformProducerErrorsAndOmissions = useSubformProducerErrorsAndOmissions({
		id: generateIdForProducerErrosAndOmissionsSubform(),
		showValidations: params.showValidations,
		props: params.props,
	});

	const subformProducerPrimaryContact = useSubformProducerPrimaryContact({
		id: generateIdForSubformProducerPrimaryContact(),
		showValidations: params.showValidations,
		props: params.props,
	});

	const subformProducerAgreement = useSubformProducerAgreement({
		id: generateIdForAgreementSubform(),
		showValidations: params.showValidations,
		props: params.props,
	});

	const [proceedToVerifyMailingAddress, setProceedToVerifyMailingAddress] = React.useState<boolean>(false);
	
	// #endregion variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		// If validations are to be shown, then initially scroll to the first invalidated input.
		if ( !params.showValidations || checkFormValidationWithFocus()) {
			// If validations pass or are not to be shown, then initially the first input should be focused.
			subformProducerCompanyDetails.companyNameInput.focusToInput();
		}
	}, []);

	React.useEffect(() => {
		if (proceedToVerifyMailingAddress && subformProducerCompanyDetails.companyAddressVerification.addressLocationInput.value.address) {
			submitFormWithMailingAddressVerification();
			setProceedToVerifyMailingAddress((currentProceedToVerifyMailingAddress: boolean) => {
				return false;
			});
		}
	}, [proceedToVerifyMailingAddress, subformProducerCompanyDetails.companyAddressVerification.addressLocationInput.value.address]);

	// #endregion life-cycle methods.


	return {
		subformProducerCompanyDetails: subformProducerCompanyDetails,
		subformProducerErrorsAndOmissions: subformProducerErrorsAndOmissions,
		subformProducerPrimaryContact: subformProducerPrimaryContact,
		subformProducerAgreement: subformProducerAgreement,
		applyFormProps: applyFormProps,
		applyNavigationFooterProps: applyNavigationFooterProps,
	};
}

// Export parameters.
export {
	UseFormProducerSignupParameters,
}

// Export results.
export {
	UseFormProducerSignupResults,
}

export default useFormProducerSignup;